import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Phone, ArrowRight, MessageCircle, Award, Clock, Zap, Euro, Users } from "lucide-react";
import { CONTACT, IMAGES, unsplashSrcSet } from "../lib/api";
import { TrustooScoreBadge } from "./TrustooScoreBadge";
import { trackConversion } from "../lib/gtag";
import { MaskedLine } from "./motion/Reveal";

const BADGE_ICONS = [Award, Clock, Zap, Euro, Users];
const BADGE_KEYS = ["experience", "emergency", "fast", "pricing", "audience"];

export default function Hero() {
  const { t } = useTranslation();
  const ref = useRef(null);

  // Scroll-linked parallax on hero background
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.4]);

  // Mouse-driven micro-parallax on the accent grid
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      mx.set(nx * 20);
      my.set(ny * 20);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#050914] pt-24 md:pt-28 select-none"
    >
      {/* Parallax background image — deliberate spotlight treatment.
          Using an <img> inside a scaled wrapper gives smoother GPU compositing
          than background-image transforms (which cause flicker on Chrome). */}
      <motion.div
        aria-hidden
        className="absolute inset-0 origin-center overflow-hidden will-change-transform"
        style={{ y: bgY, scale: bgScale }}
      >
        <img
          src={IMAGES.hero}
          srcSet={unsplashSrcSet(IMAGES.hero)}
          sizes="100vw"
          alt=""
          loading="eager"
          fetchpriority="high"
          decoding="async"
          width="1600"
          height="1067"
          className="w-full h-full object-cover"
          style={{
            opacity: 0.42,
            filter: "grayscale(20%) contrast(1.08)",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
          }}
        />
      </motion.div>
      {/* Editorial spotlight vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 55% at 68% 45%, rgba(255,90,31,0.14) 0%, rgba(10,17,40,0) 55%)",
        }}
      />
      {/* Base ink gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#050914] via-[#0A1128]/95 to-[#0A1128]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-transparent to-transparent" />
      {/* Scroll-linked shade */}
      <motion.div className="absolute inset-0 bg-[#050914]" style={{ opacity: overlayOpacity }} />
      {/* Fine grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      {/* Editorial grid guides — asymmetric */}
      <motion.div
        aria-hidden
        style={{ x: smx, y: smy }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute top-24 right-0 w-40 h-40 md:w-72 md:h-72 border-r border-t border-[#FF5A1F]/25" />
        <div className="absolute bottom-0 left-0 w-32 h-32 md:w-56 md:h-56 border-l border-b border-[#FF5A1F]/25" />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-[#FF5A1F]" />
        <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-[#FF5A1F]/60" />
      </motion.div>

      {/* Vertical editorial label — right rail */}
      <div className="hidden lg:flex absolute top-1/2 right-6 -translate-y-1/2 items-center gap-3 [writing-mode:vertical-rl] rotate-180 text-white/40 text-[11px] uppercase tracking-[0.4em] font-bold pointer-events-none">
        <span>Ataiwo · EST. Amsterdam</span>
        <span className="w-8 h-px bg-[#FF5A1F]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-8 items-center py-12 md:py-20 w-full">
        <div className="lg:col-span-9 text-white">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-3 mb-6 md:mb-8 bg-white/[0.04] border border-white/10 px-4 py-2 backdrop-blur-sm"
          >
            <span className="w-2 h-2 bg-[#FF5A1F] animate-pulse rounded-full" />
            <span className="text-white uppercase font-bold tracking-widest text-[11px] md:text-xs">
              {t("hero.eyebrow")}
            </span>
          </motion.div>

          {/* Kinetic masked headline */}
          <h1 className="font-display text-[2.4rem] leading-[0.98] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[6.5rem] 2xl:text-[7.5rem] text-white mb-5 md:mb-6 max-w-6xl -tracking-[0.03em]">
            <span className="block overflow-hidden">
              <MaskedLine text={t("hero.title_1")} delay={0.15} wordDelay={0.08} />
            </span>
            <span className="block overflow-hidden text-[#FF5A1F]">
              <MaskedLine text={t("hero.title_2")} delay={0.32} wordDelay={0.06} />
            </span>
            <span className="block overflow-hidden">
              <MaskedLine text={t("hero.title_3")} delay={0.5} wordDelay={0.05} />
            </span>
            <span className="block overflow-hidden">
              <MaskedLine text={t("hero.title_4")} delay={0.68} wordDelay={0.05} />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="flex items-center gap-3 mb-6 md:mb-8"
            data-testid="hero-tagline"
          >
            <span className="w-6 h-px bg-[#FF5A1F]" />
            <span className="font-display text-white text-sm md:text-base uppercase tracking-[0.25em]">
              {t("hero.tagline")}
            </span>
            <span className="w-6 h-px bg-[#FF5A1F]" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-base sm:text-lg md:text-xl text-white/85 max-w-2xl mb-8 md:mb-10 leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-8 md:mb-10"
          >
            <a href="#offerte" className="btn-primary btn-lg group" data-testid="hero-quote-btn">
              {t("hero.cta_quote")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a href={`tel:${CONTACT.phone}`} className="btn-outline-light btn-lg" data-testid="hero-call-btn" onClick={() => trackConversion("phone_click", { location: "hero" })}>
              <Phone className="w-4 h-4" /> {t("hero.cta_call", { phone: CONTACT.phoneDisplay })}
            </a>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-light btn-lg"
              data-testid="hero-whatsapp-btn"
              onClick={() => trackConversion("whatsapp_click", { location: "hero" })}
            >
              <MessageCircle className="w-4 h-4" /> {t("hero.cta_whatsapp")}
            </a>
          </motion.div>

          {/* Trustoo score badge */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.4 }}
            className="mb-12 md:mb-14"
            data-testid="hero-trustoo-score"
          >
            <TrustooScoreBadge variant="dark" />
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 1.5 } } }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 border-t border-white/10 pt-6 md:pt-8 max-w-4xl"
          >
            {BADGE_KEYS.map((k, i) => {
              const Icon = BADGE_ICONS[i];
              return (
                <motion.div
                  key={k}
                  data-testid={`hero-badge-${i}`}
                  variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.55 } } }}
                  className="flex items-center gap-2.5"
                >
                  <span className="w-9 h-9 shrink-0 bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#FF5A1F]" strokeWidth={2.4} />
                  </span>
                  <span className="text-[11px] md:text-xs text-white/85 uppercase tracking-wide font-bold leading-tight">
                    {t(`hero.badges.${k}`)}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-1 text-white/40 text-[10px] uppercase tracking-widest font-bold pointer-events-none">
        <span>{t("hero.scroll")}</span>
        <span className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent animate-scroll-cue" />
      </div>
    </section>
  );
}
